import { Link } from "react-router-dom";
import { NavIcon } from "../components/NavIcon";
import { useDeployment } from "../deployment/DeploymentContext";
import { useOnboarding } from "../onboarding/useOnboarding";

/** The new-tenant welcome: a live checklist (ticked from what actually exists), how the pieces fit
 *  together, and a copy-ready API call. Works the same on the hosted SaaS and on-prem. */
export default function GettingStarted() {
  const { steps, loading, doneCount, firstReportKey } = useOnboarding();
  const { isOnPrem } = useDeployment();
  const origin = window.location.origin;
  const key = firstReportKey ?? "REPORT_KEY";
  const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  const curl = `curl -X POST "${origin}/api/v1/reports/${key}/download" \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{"params": {"dateFrom": "2026-01-01", "dateTo": "2026-01-31"}, "format": "pdf"}' \\
  --output report.pdf`;

  return (
    <div>
      <div className="dashboard-hero">
        <div>
          <h1>Welcome — let's get you set up</h1>
          <p>Follow these steps and you'll have a live, branded report in a few minutes. Your progress updates as you go.</p>
        </div>
      </div>

      <div className="page-panel">
        <div className="onboarding-progress-head">
          <h3 style={{ margin: 0 }}>Your setup checklist</h3>
          <span className="help-text">{loading ? "Checking…" : `${doneCount} of ${steps.length} done`}</span>
        </div>
        <div className="onboarding-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="onboarding-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <ol className="onboarding-steps">
          {steps.map((s, i) => (
            <li key={s.id} className={s.done ? "onboarding-step onboarding-step-done" : "onboarding-step"}>
              <div className="onboarding-step-mark">{s.done ? <NavIcon name="checkCircle" size={22} /> : <span>{i + 1}</span>}</div>
              <div className="onboarding-step-body">
                <div className="onboarding-step-title">
                  {s.title}
                  {s.optional && <span className="badge">optional</span>}
                </div>
                <div className="help-text">{s.why}</div>
              </div>
              <Link to={s.to} className={s.done ? "btn-secondary onboarding-cta" : "btn-primary onboarding-cta"}>
                {s.done ? "Review" : s.cta}
              </Link>
            </li>
          ))}
        </ol>
      </div>

      <div className="onboarding-two-col">
        <div className="page-panel">
          <h3>How it fits together</h3>
          <ol className="onboarding-flow">
            <li><strong>Data</strong><span>Your system exposes the numbers as JSON. ReportForge fetches them — or you send them with each request.</span></li>
            <li><strong>Mapping</strong><span>Map each field to a name the template can use, or map a whole list to build a repeating table.</span></li>
            <li><strong>Template</strong><span>Design the layout with variables, snippets and a live preview. Start from a starter template.</span></li>
            <li><strong>Output</strong><span>Download as PDF, Excel, Word or CSV; embed it; or schedule it by email.</span></li>
          </ol>
          <p className="help-text">Report keys (like <code>CASHBOOK_PAYMENT</code>) are what your application uses to ask for a report — they're set once and never change.</p>
        </div>

        <div className="page-panel">
          <h3>Request a report from your own application</h3>
          <p className="help-text">
            Create an API key, then call the report by its key. {firstReportKey ? <>Shown here with your report <code>{firstReportKey}</code>.</> : <>Replace <code>REPORT_KEY</code> with one of your report keys.</>}
          </p>
          <div className="code-block">{curl}</div>
          <p className="help-text" style={{ marginTop: 10 }}>
            More ways to integrate (embed, JavaScript) are on the <Link to="/integration-sample">Integration Sample</Link> page.
          </p>
        </div>
      </div>

      <div className="page-panel">
        <h3>Good to know</h3>
        <ul className="onboarding-tips">
          <li><strong>Roles.</strong> Owners and admins manage everything; designers build reports; viewers can only run them.</li>
          <li><strong>Drafts are safe.</strong> Changes autosave as a draft — nothing goes live until you press Publish.</li>
          <li><strong>Snippets.</strong> In the designer, save any block you like as a snippet and reuse it in other reports.</li>
          {!isOnPrem && <li><strong>Plans.</strong> You're on a free trial; compare plans and upgrade any time under Billing.</li>}
          <li><strong>Your account.</strong> Change your password from <Link to="/account">Account</Link> (click your email at the bottom-left).</li>
        </ul>
      </div>
    </div>
  );
}
